import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { useAdditionalFormContext } from '../../../common/form';
import { encodeAlgorithmMappingOptionValue } from '../mappings/utils';
import { encodeAlgorithmSourceDimensionOptionValue } from '../sources/utils';
import {
  AlgorithmJoinItemSelectField,
  AlgorithmJoinItemSelectFieldProps,
} from './AlgorithmJoinItemSelectField';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import {
  AlgorithmJoinItemValidFormDataType,
  AlgorithmJoinsValidFormDataType,
} from './schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinItemTargetDimensionOrMappingSelectField.translations.json';
import { isDimensionWithCategories } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-target-dimension-select-field',
);
addTranslations(translations);

export interface AlgorithmJoinItemTargetDimensionOrMappingSelectFieldProps
  extends Omit<
    AlgorithmJoinItemSelectFieldProps,
    'name' | 'label' | 'value' | 'onSelect' | 'onClear'
  > {
  joinIndex: number;
  index: number;
  onChange?: (value: string | number, option?: any) => void;
}

export const AlgorithmJoinItemTargetDimensionOrMappingSelectField: FC<
  AlgorithmJoinItemTargetDimensionOrMappingSelectFieldProps
> = (props) => {
  const { joinIndex, index, onChange, ...rest } = props;
  const { t } = useTranslation();

  const { getValues, setValue } =
    useFormContext<AlgorithmJoinsValidFormDataType>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const name = `joins.${joinIndex}.algorithmJoinItems.${index}` as const;
  const value = useWatch<AlgorithmJoinsValidFormDataType, typeof name>({
    name,
  });
  const [algorithmSourcesByIds] = AlgorithmJoinsFormStore.useValue(
    'algorithmSourcesByIds',
  );

  const selectedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    if (
      value._variant?.endsWith('_MAPPING') &&
      'algorithmMappingAlias' in value &&
      value.algorithmMappingAlias
    ) {
      return encodeAlgorithmMappingOptionValue(
        value.algorithmMappingAlias as string,
      );
    }

    if ('targetDimension' in value && value.targetDimension) {
      const algorithmSource = algorithmSourcesByIds.get(
        getValues(`joins.${joinIndex}.algorithmSourceId`),
      )!;
      const dimension = algorithmSource.dimensions.find(
        (d) => d.code === value.targetDimension,
      )!;
      return encodeAlgorithmSourceDimensionOptionValue(
        algorithmSource,
        dimension,
      );
    }

    return '';
  }, [value, getValues, joinIndex, algorithmSourcesByIds]);

  const handleSelect: AlgorithmJoinItemSelectFieldProps['onSelect'] =
    useCallback(
      (value, option) => {
        const current = getValues(name);

        if (!current) {
          return;
        }

        const base = {
          [ROW_UNIQUE_KEY_NAME]: current[ROW_UNIQUE_KEY_NAME],
          id: current.id,
        };

        let next: AlgorithmJoinItemValidFormDataType | undefined = undefined;

        if ('dimension' in option) {
          const newDimensionCode = option.dimension.code;
          const withCategories = isDimensionWithCategories(option.dimension);

          if (
            current._variant === 'DIMENSION_DIMENSION' ||
            current._variant === 'DIMENSION_MAPPING'
          ) {
            next = {
              ...base,
              _variant: 'DIMENSION_DIMENSION',
              algorithmSourceId: current.algorithmSourceId,
              sourceDimension: current.sourceDimension,
              targetDimension: newDimensionCode,
            };
          } else if (current._variant === 'MAPPING_DIMENSION') {
            next = {
              ...base,
              _variant: 'MAPPING_DIMENSION',
              algorithmMappingAlias: current.algorithmMappingAlias,
              targetDimension: newDimensionCode,
            };
          } else if (current._variant === 'VALUE_DIMENSION') {
            next = {
              ...base,
              _variant: 'VALUE_DIMENSION',
              sourceValue: withCategories ? '' : current.sourceValue,
              sourceValueFormat: current.sourceValueFormat,
              sourceValueDataType: current.sourceValueDataType,
              targetDimension: newDimensionCode,
            };
          } else if (current._variant === 'VALUE_MAPPING') {
            next = {
              ...base,
              _variant: 'VALUE_DIMENSION',
              sourceValue: '',
              targetDimension: newDimensionCode,
            };
          }
        } else if ('mapping' in option) {
          if (
            current._variant === 'DIMENSION_DIMENSION' ||
            current._variant === 'DIMENSION_MAPPING'
          ) {
            next = {
              ...base,
              _variant: 'DIMENSION_MAPPING',
              algorithmSourceId: current.algorithmSourceId,
              sourceDimension: current.sourceDimension,
              algorithmMappingAlias: option.mapping.alias,
            };
          } else if (
            current._variant === 'VALUE_DIMENSION' ||
            current._variant === 'VALUE_MAPPING'
          ) {
            next = {
              ...base,
              _variant: 'VALUE_MAPPING',
              sourceValue: '',
              algorithmMappingAlias: option.mapping.alias,
            };
          }
        }

        if (!next) {
          throw new Error(
            `Unexpected variant "${current._variant}" or option shape in target dimension/mapping select:\n${JSON.stringify(option, null, 2)}`,
          );
        }

        setValue(name, next, { shouldDirty: true });
        onChange?.(value, option);
        formOnChange?.();
      },
      [name, onChange, getValues, setValue, formOnChange],
    );

  const handleClear: AlgorithmJoinItemSelectFieldProps['onClear'] =
    useCallback(() => {
      const current = getValues(name);

      if (!current) {
        return;
      }

      const base = {
        [ROW_UNIQUE_KEY_NAME]: current[ROW_UNIQUE_KEY_NAME],
        id: current.id,
      };

      let next: AlgorithmJoinItemValidFormDataType | undefined;

      if (
        current._variant === 'DIMENSION_DIMENSION' ||
        current._variant === 'DIMENSION_MAPPING'
      ) {
        next = {
          ...base,
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId: current.algorithmSourceId,
          sourceDimension: current.sourceDimension,
          targetDimension: '',
        };
      } else if (current._variant === 'MAPPING_DIMENSION') {
        next = {
          ...base,
          _variant: 'MAPPING_DIMENSION',
          algorithmMappingAlias: current.algorithmMappingAlias,
          targetDimension: '',
        };
      } else if (current._variant === 'VALUE_DIMENSION') {
        next = {
          ...base,
          _variant: 'VALUE_DIMENSION',
          sourceValue: current.sourceValue,
          sourceValueFormat: current.sourceValueFormat,
          sourceValueDataType: current.sourceValueDataType,
          targetDimension: '',
        };
      } else if (current._variant === 'VALUE_MAPPING') {
        next = {
          ...base,
          _variant: 'VALUE_DIMENSION',
          sourceValue: '',
          targetDimension: '',
        };
      }

      if (!next) {
        throw new Error(
          `Unexpected variant "${current._variant}" in target dimension/mapping clear`,
        );
      }

      setValue(name, next, { shouldDirty: true });
      onChange?.(undefined!, undefined!);
      formOnChange?.();
    }, [name, onChange, getValues, setValue, formOnChange]);

  const fieldName = value?._variant?.endsWith('_MAPPING')
    ? 'algorithmMappingAlias'
    : 'targetDimension';

  return (
    <AlgorithmJoinItemSelectField
      name={`${name}.${fieldName}`}
      label={t('target-dimension')}
      value={selectedValue}
      onSelect={handleSelect}
      onClear={handleClear}
      {...rest}
    />
  );
};
